import React, { useEffect } from 'react'

import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, Checkbox, Chip, Divider, FormControlLabel, Grid, IconButton, Stack, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import Modal from '../../../../../../shared/components/Modal/Modal.component'

import { getLockerOrderDetails } from '../../../../../../redux/states/admin/lockers/getLockerOrderDetails/getLockerOrderDetails.slice'
import { RootState } from '../../../../../../redux/store/store.types'
import date, { dateTimeFormats } from '../../../../../../shared/functions/Date/date.functions'
import { formatAdminLockerName, getLockerNumberLabel } from '../../../../../../shared/functions/Locker/locker.functions'
import {
  joinElementsWithBullet,
  joinStringsWithBullet
} from '../../../../../../shared/functions/String/string.functions'
import { useAppDispatch } from '../../../../../../shared/hooks/redux.hooks'
import theme from '../../../../../../shared/styles/themes/default.theme'
import { OrderDetailModalProps } from './OrderDetailModal.types'

const renderLabel = (label: string, value?: string) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600}>
      {value || '—'}
    </Typography>
  </Stack>
)

const renderCodeRow = (label: string, value: string, onCopy: () => void) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Typography variant="body2">
      <strong>{label}:</strong> {value}
    </Typography>
    <IconButton size="small" onClick={onCopy}>
      <ContentCopyIcon fontSize="small" />
    </IconButton>
  </Stack>
)

const getStatusChipProps = (status: string) => {
  const statusLower = status.toLowerCase()

  switch (statusLower) {
    case 'pending':
    case 'dropoff_pending':
      return {
        label: 'Dropoff Pending',
        color: theme.palette.warning.main,
        backgroundColor: theme.palette.warning.light
      }
    case 'pickup_pending':
      return {
        label: 'Pickup Pending',
        color: theme.palette.info.main,
        backgroundColor: theme.palette.info.light
      }
    case 'completed':
      return {
        label: 'Completed',
        color: theme.palette.success.main,
        backgroundColor: theme.palette.success.light
      }
    case 'cancelled':
      return {
        label: 'Cancelled',
        color: theme.palette.error.main,
        backgroundColor: theme.palette.error.light
      }
    default:
      return {
        label: status,
        color: theme.palette.grey[700],
        backgroundColor: theme.palette.grey[200]
      }
  }
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({ open, boxId, onClose }) => {
  const dispatch = useAppDispatch()
  const { success: orderDetails, loading } = useSelector((state: RootState) => state.getLockerOrderDetails)

  useEffect(() => {
    if (open && boxId) {
      dispatch(getLockerOrderDetails({ request: { boxId } }))
    }
  }, [open, boxId, dispatch])

  // Name the locker from the loaded order, which carries its number, rather than the door ID the box was opened with
  const doorAssignment = orderDetails?.data?.doorAssignment
  const fallbackLockerName = boxId ? formatAdminLockerName({ doorId: boxId }) : ''
  const lockerName = doorAssignment ? formatAdminLockerName(doorAssignment) : fallbackLockerName

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code)
  }

  const renderOrderDetailsBody = () => {
    if (loading) {
      return <Typography>Loading order details...</Typography>
    }

    if (!orderDetails?.data) {
      return <Typography>No order details available</Typography>
    }

    const data = orderDetails.data

    const travelerEmailLink = (
      <a href={`mailto:${data.traveler.email}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
        {data.traveler.email}
      </a>
    )

    const travelerPhoneLink = data.traveler.phoneNumber ? (
      <a href={`tel:${data.traveler.phoneNumber}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
        {data.traveler.phoneNumber}
      </a>
    ) : null

    const shopperEmailLink = (
      <a href={`mailto:${data.shopper.email}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
        {data.shopper.email}
      </a>
    )

    const shopperPhoneLink = data.shopper.phoneNumber ? (
      <a href={`tel:${data.shopper.phoneNumber}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
        {data.shopper.phoneNumber}
      </a>
    ) : null

    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Traveler:
          </Typography>

          <Typography variant="body2">
            {joinElementsWithBullet([data.traveler.name, data.traveler.userId, travelerEmailLink, travelerPhoneLink])}
          </Typography>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Shopper:
          </Typography>

          <Typography variant="body2">
            {joinElementsWithBullet([data.shopper.name, data.shopper.userId, shopperEmailLink, shopperPhoneLink])}
          </Typography>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Item Details
          </Typography>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              {renderLabel('Item Request ID', data.item.itemRequestId)}
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel('Offer Request ID', data.item.offerRequestId)}
            </Grid>

            <Grid item xs={12} md={4}>
              {renderLabel('Trip ID', data.item.tripId || '—')}
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={0.5}>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>

                <Box>
                  {(() => {
                    // If dropoff timestamp exists but pickup doesn't, status should be pickup_pending
                    // Otherwise use the actual status from the API
                    const displayStatus =
                      data.item.dropoffTimestamp && !data.item.pickupTimestamp ? 'pickup_pending' : data.item.status
                    const chipProps = getStatusChipProps(displayStatus)

                    return (
                      <Chip
                        label={chipProps.label}
                        size="small"
                        sx={{
                          backgroundColor: chipProps.backgroundColor,
                          color: chipProps.color,
                          fontWeight: 600
                        }}
                      />
                    )
                  })()}
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox checked={!!data.item.dropoffTimestamp} readOnly disabled={!data.item.dropoffTimestamp} />
                }
                label={
                  <Typography variant="body2">
                    Dropped Off
                    {data.item.dropoffTimestamp && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {date(data.item.dropoffTimestamp).format(dateTimeFormats.date.medium)}
                      </Typography>
                    )}
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Checkbox checked={!!data.item.pickupTimestamp} readOnly disabled={!data.item.pickupTimestamp} />
                }
                label={
                  <Typography variant="body2">
                    Picked Up
                    {data.item.pickupTimestamp && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {new Date(data.item.pickupTimestamp).toLocaleString()}
                      </Typography>
                    )}
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Locker Codes
          </Typography>

          <Stack spacing={1}>
            {renderCodeRow('Dropoff Code', data.lockerCodes.dropoffCode, () =>
              handleCopyCode(data.lockerCodes.dropoffCode)
            )}

            {renderCodeRow('Pickup Code', data.lockerCodes.pickupCode, () =>
              handleCopyCode(data.lockerCodes.pickupCode)
            )}

            {data.lockerCodes.codeExpiry && (
              <Typography variant="caption" color="text.secondary">
                Expires: {new Date(data.lockerCodes.codeExpiry).toLocaleString()}
              </Typography>
            )}
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>
            Locker Assignment
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              {renderLabel('Locker', getLockerNumberLabel(data.doorAssignment))}
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel('Door ID', data.doorAssignment.doorId)}
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel('Cabinet', data.doorAssignment.cabinetId)}
            </Grid>

            <Grid item xs={12} md={3}>
              {renderLabel('Size', data.doorAssignment.sizeId)}
            </Grid>
          </Grid>
        </Box>
      </Stack>
    )
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={joinStringsWithBullet(['Order Details', lockerName])}
      maxWidth="lg"
      body={renderOrderDetailsBody()}
      primaryButton={{
        label: 'Close',
        onClick: onClose
      }}
    />
  )
}

export default OrderDetailModal
